import { useCallback, useEffect, useRef, useState } from 'react';
import type { Feedback } from '../components/InlineFeedback';

// Feedback state for InlineFeedback. Success/info auto-clear; errors stay
// until the next action or an explicit clear().
export function useFeedback(autoClearMs = 4000) {
  const [feedback, setFeedback] = useState<Feedback | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  const show = useCallback(
    (next: Feedback) => {
      if (timerRef.current) clearTimeout(timerRef.current);
      setFeedback(next);
      if (next.kind !== 'error' && autoClearMs > 0) {
        timerRef.current = setTimeout(() => setFeedback(null), autoClearMs);
      }
    },
    [autoClearMs]
  );

  const showSuccess = useCallback((message: string) => show({ kind: 'success', message }), [show]);
  const showError = useCallback((message: string) => show({ kind: 'error', message }), [show]);
  const showInfo = useCallback((message: string) => show({ kind: 'info', message }), [show]);
  const clear = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setFeedback(null);
  }, []);

  return { feedback, showSuccess, showError, showInfo, clear };
}
